import { useState, useEffect, type ReactNode, type KeyboardEvent } from 'react';
import SystemHeader from '@/widgets/layout/SystemHeader';
import { Card, Skeleton } from 'antd';
import { dashboardApi } from '@/services/dashboard/api';
import type { DashboardStatsResponse, SectionStats } from '@/services/dashboard/types';
import { useAppNavigate } from '@/hooks/useAppNavigate';
import type { NavOpts } from '@/routes';
import { CASE_STATUS_KEYS, CASE_STATUS_CODE_MAP, type CaseStatus } from '@/constants/enums';
import { isValidHexColor, readableTextColor } from '@/utils/color';

interface BusinessReportProps {
  embedded?: boolean;
}

interface KpiItem {
  label: string;
  value: string;
  icon?: string;
  valueClass?: string;
  linkTo?: string;
  linkOpts?: NavOpts;
}

type WelfareScope = 'all' | 'active' | 'ended';
type DashTabId = 'overall' | 'active' | 'ended';
type DashLayout = 'tabs' | 'stack';

const fmt = (n: number | null | undefined) => (n == null ? '—' : Number(n).toLocaleString());

const KpiCards = ({
  items,
  columnsClass,
  onItemClick,
}: {
  items: KpiItem[];
  columnsClass: string;
  onItemClick?: (item: KpiItem) => void;
}) => (
  <div className={`grid ${columnsClass} gap-4`}>
    {items.map((s, i) => {
      const clickable = Boolean(s.linkTo) && Boolean(onItemClick);
      const handleClick = () => {
        if (clickable && onItemClick) onItemClick(s);
      };
      const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (!clickable) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          handleClick();
        }
      };
      return (
        <Card
          key={i}
          size="small"
          hoverable={clickable}
          role={clickable ? 'button' : undefined}
          tabIndex={clickable ? 0 : undefined}
          onClick={clickable ? handleClick : undefined}
          onKeyDown={clickable ? handleKeyDown : undefined}
          className={clickable ? 'cursor-pointer transition-shadow' : undefined}
        >
          <div className="flex justify-between items-start gap-2">
            <div className="min-w-0 flex flex-col">
              <p className="text-xs text-gray-500 leading-snug min-h-[2.5rem] flex items-end pb-1">{s.label}</p>
              <p
                className={`text-2xl sm:text-3xl font-bold ${s.valueClass || 'text-gray-800'} ${
                  clickable ? 'underline decoration-dotted underline-offset-4' : ''
                }`}
              >
                {s.value}
              </p>
            </div>
            {s.icon ? <span className="text-2xl shrink-0">{s.icon}</span> : null}
          </div>
        </Card>
      );
    })}
  </div>
);

const SectionHeading = ({ icon, children }: { icon?: string; children: ReactNode }) => (
  <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-200">
    {icon ? (
      <span className="text-xl" aria-hidden>
        {icon}
      </span>
    ) : null}
    <h3 className="text-base font-bold text-gray-800">{children}</h3>
  </div>
);

const ALL_STATUS_KEYS: CaseStatus[] = [...CASE_STATUS_KEYS];
const PAID_STATUSES: CaseStatus[] = [
  'ZENGIN_PROD_EXPORTED',
  'COMPLETED_CASH',
  'COMPLETED_PLAINTIFF_CALC',
];
const PENDING_STATUSES: CaseStatus[] = [
  'AWAITING_TRANSFER_LIST',
  'AWAITING_CALC',
  'CALC_IN_PROGRESS',
  'CALC_ERROR',
  'CALC_DONE',
  'NOTICE_PRINTED',
  'NOTICE_DOWNLOADED',
];
const APPLICATION_SUBMITTED_STATUSES: CaseStatus[] = ALL_STATUS_KEYS.filter((k) => {
  return !['IDENT_ID_UNEXPORTED', 'APP_AWAITING_SUBMIT'].includes(k);
});
const APPLICATION_AWAITING_REVIEW_STATUSES: CaseStatus[] = ['APP_UNDER_REVIEW'];
const APPLICATION_APPROVED_STATUSES: CaseStatus[] = ALL_STATUS_KEYS.filter((k) => {
  if (k === 'COMPLETED_DENIED') return false;
  return ![
    'IDENT_ID_UNEXPORTED',
    'APP_AWAITING_SUBMIT',
    'APP_UNDER_REVIEW',
    'APP_REJECTED',
  ].includes(k);
});
const APPLICATION_REJECTED_STATUSES: CaseStatus[] = ['APP_REJECTED'];
const APPLICATION_DENIED_STATUSES: CaseStatus[] = ['COMPLETED_DENIED'];

const toStatusCodes = (keys: CaseStatus[]): string[] =>
  keys.map((k) => String(CASE_STATUS_CODE_MAP[k]));

const buildSectionCards = (
  s: SectionStats | undefined | null,
  welfare: WelfareScope,
): KpiItem[] => {
  const paidStatuses = PAID_STATUSES;
  const pendingLinkOpts: NavOpts | undefined =
    welfare === 'all'
      ? {
          recipientSearchPreset: {
            dashboardPreset: 'overallPending',
            statuses: toStatusCodes(PENDING_STATUSES),
          },
        }
      : { recipientSearchPreset: { welfare, statuses: toStatusCodes(PENDING_STATUSES) } };
  const applicationWelfare: WelfareScope = welfare === 'all' ? 'ended' : welfare;

  const targetCard: KpiItem = {
    label: '追加給付対象世帯数',
    value: fmt(s?.targetHouseholds),
    icon: '🏠',
    valueClass: 'text-blue-600',
    linkTo: 'recipient-search',
    linkOpts: { recipientSearchPreset: { welfare } },
  };
  const paidCard: KpiItem = {
    label: '追加給付支給済',
    value: fmt(s?.paidHouseholds),
    icon: '✅',
    valueClass: 'text-green-600',
    linkTo: 'recipient-search',
    linkOpts: { recipientSearchPreset: { welfare, statuses: toStatusCodes(paidStatuses) } },
  };
  const pendingCard: KpiItem = {
    label: '対応中',
    value: fmt(s?.pendingHouseholds),
    icon: '⏳',
    valueClass: 'text-amber-600',
    linkTo: 'recipient-search',
    linkOpts: pendingLinkOpts,
  };

  if (s?.applicationCount == null) {
    return [targetCard, paidCard, pendingCard];
  }

  const submittedCard: KpiItem = {
    label: '申出書提出',
    value: fmt(s.applicationCount),
    icon: '📄',
    valueClass: 'text-indigo-600',
    linkTo: 'recipient-search',
    linkOpts: {
      recipientSearchPreset: { welfare: applicationWelfare, statuses: toStatusCodes(APPLICATION_SUBMITTED_STATUSES) },
    },
  };
  const awaitingReviewCard: KpiItem = {
    label: welfare === 'ended' ? '未審査' : '未審査',
    value: fmt(s.applicationAwaitingReviewCount),
    icon: '🕒',
    valueClass: 'text-slate-600',
    linkTo: 'recipient-search',
    linkOpts: {
      recipientSearchPreset: { welfare: applicationWelfare, statuses: toStatusCodes(APPLICATION_AWAITING_REVIEW_STATUSES) },
    },
  };
  const approvedCard: KpiItem = {
    label: '承認',
    value: fmt(s.approvedCount),
    icon: '👍',
    valueClass: 'text-teal-600',
    linkTo: 'recipient-search',
    linkOpts: {
      recipientSearchPreset: { welfare: applicationWelfare, statuses: toStatusCodes(APPLICATION_APPROVED_STATUSES) },
    },
  };
  const rejectedCard: KpiItem = {
    label: '差戻',
    value: fmt(s.rejectedCount),
    icon: '↩️',
    valueClass: 'text-orange-600',
    linkTo: 'recipient-search',
    linkOpts: {
      recipientSearchPreset: { welfare: applicationWelfare, statuses: toStatusCodes(APPLICATION_REJECTED_STATUSES) },
    },
  };
  const deniedCard: KpiItem = {
    label: '不支給決定',
    value: fmt(s.deniedCount),
    icon: '🚫',
    valueClass: 'text-red-600',
    linkTo: 'recipient-search',
    linkOpts: {
      recipientSearchPreset: { welfare: applicationWelfare, statuses: toStatusCodes(APPLICATION_DENIED_STATUSES) },
    },
  };

  if (welfare === 'ended') {
    return [
      targetCard,
      submittedCard,
      awaitingReviewCard,
      rejectedCard,
      deniedCard,
      approvedCard,
      paidCard,
      pendingCard,
    ];
  }

  return [
    targetCard,
    paidCard,
    pendingCard,
    submittedCard,
    awaitingReviewCard,
    approvedCard,
    rejectedCard,
    deniedCard,
  ];
};

const TAB_DEFS: { id: DashTabId; label: string; scope: WelfareScope; icon: string }[] = [
  { id: 'overall', label: '全体', scope: 'all', icon: '📊' },
  { id: 'active', label: '受給中世帯', scope: 'active', icon: '🟢' },
  { id: 'ended', label: '保護廃止世帯', scope: 'ended', icon: '🔴' },
];

const columnsForScope = (scope: WelfareScope) =>
  scope === 'active'
    ? 'grid-cols-1 sm:grid-cols-3'
    : 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-8';

const BusinessReport = ({ embedded }: BusinessReportProps) => {
  const navigate = useAppNavigate();
  const [stats, setStats] = useState<DashboardStatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [dashTab, setDashTab] = useState<DashTabId>('overall');
  const [dashLayout, setDashLayout] = useState<DashLayout>('tabs');

  useEffect(() => {
    dashboardApi
      .getStats()
      .then(({ data }) => setStats(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleKpiClick = (item: KpiItem) => {
    if (item.linkTo) navigate(item.linkTo, item.linkOpts);
  };

  if (loading) {
    const skeleton = (
      <div className="space-y-6">
        <Skeleton active paragraph={{ rows: 2 }} />
        <Skeleton active paragraph={{ rows: 2 }} />
        <Skeleton active paragraph={{ rows: 2 }} />
      </div>
    );
    if (embedded) return <div className="min-w-0">{skeleton}</div>;
    return (
      <div className="min-h-screen bg-gray-100">
        <SystemHeader breadcrumbs={[{ label: '業務報告' }]} />
        <div className="max-w-7xl mx-auto px-6 py-8">{skeleton}</div>
      </div>
    );
  }

  const sectionByScope = (scope: WelfareScope): SectionStats | undefined => {
    if (scope === 'all') return stats?.overall;
    if (scope === 'active') return stats?.activeWelfare;
    return stats?.endedWelfare;
  };

  const activeTab = TAB_DEFS.find((t) => t.id === dashTab) ?? TAB_DEFS[0];

  const dashboardControls = (
    <div className="mb-4 flex flex-col gap-3">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-800">ダッシュボード</h2>
          <p className="text-sm text-gray-600 mt-1">
            {dashLayout === 'tabs'
              ? 'タブで区分を切り替えて業務状況を確認できます'
              : '全体・受給中世帯・保護廃止世帯を上から順に表示しています'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-gray-500 shrink-0">レイアウト</span>
          <div className="flex gap-1 p-1 bg-slate-200/90 rounded-lg w-fit">
            <button
              type="button"
              onClick={() => setDashLayout('tabs')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition cursor-pointer whitespace-nowrap ${
                dashLayout === 'tabs' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              タブ
            </button>
            <button
              type="button"
              onClick={() => setDashLayout('stack')}
              className={`px-3 py-1.5 text-sm font-medium rounded-md transition cursor-pointer whitespace-nowrap ${
                dashLayout === 'stack' ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-600 hover:text-slate-900'
              }`}
            >
              縦並び
            </button>
          </div>
        </div>
      </div>
      {dashLayout === 'tabs' ? (
        <div className="flex flex-wrap gap-1 p-1 bg-gray-200/80 rounded-lg w-fit max-w-full">
          {TAB_DEFS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setDashTab(t.id)}
              className={`px-3 py-2 text-sm font-medium rounded-md transition cursor-pointer whitespace-nowrap ${
                dashTab === t.id ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );

  // No.27: 全世帯（全体）の事務局確認用ステータス別内訳（色分けカード）。カードクリックで
  // 全体×当該ステータスに絞り込んだ受給者一覧へ遷移する（未設定は sentinel 0）。
  // ※関数名/プロパティ名は歴史的経緯で active のままだが集計口径は全体（後方互換維持のため改名しない）。
  const renderActiveOfficeReviewBreakdown = () => {
    const breakdown = stats?.activeOfficeReviewBreakdown;
    if (!breakdown || breakdown.length === 0) return null;
    return (
      <div className="mt-6">
        <div className="mb-3 flex flex-wrap items-baseline gap-2">
          <span className="text-sm font-bold text-gray-700">事務局確認用ステータス別内訳</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-6 gap-3">
          {breakdown.map((b) => {
            const bg = isValidHexColor(b.color) ? b.color : '#8c8c8c';
            const fg = readableTextColor(bg);
            const handleClick = () =>
              navigate('recipient-search', {
                recipientSearchPreset: { welfare: 'all', officeReviewStatusId: b.officeReviewStatusId ?? 0 },
              });
            return (
              <div
                key={b.officeReviewStatusId ?? 'unset'}
                role="button"
                tabIndex={0}
                onClick={handleClick}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleClick();
                  }
                }}
                className="cursor-pointer overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm transition hover:shadow-md"
              >
                <div className="h-1.5" style={{ backgroundColor: bg }} />
                <div className="px-3 py-2">
                  <div className="mb-1 flex items-center gap-1.5">
                    <span
                      className="inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium"
                      style={{ backgroundColor: bg, color: fg }}
                      title={b.displayName}
                    >
                      <span className="max-w-[7rem] truncate">{b.displayName}</span>
                    </span>
                  </div>
                  <p className="text-2xl font-bold text-gray-800 underline decoration-dotted underline-offset-4">
                    {fmt(b.count)}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const renderTabKpis = (scope: WelfareScope) => (
    <>
      <KpiCards
        items={buildSectionCards(sectionByScope(scope), scope)}
        columnsClass={columnsForScope(scope)}
        onItemClick={handleKpiClick}
      />
      {scope === 'all' ? renderActiveOfficeReviewBreakdown() : null}
    </>
  );

  const stackBody = (
    <div className="space-y-10">
      {TAB_DEFS.map((t) => (
        <section key={t.id}>
          <SectionHeading icon={t.icon}>{t.label}</SectionHeading>
          {renderTabKpis(t.scope)}
        </section>
      ))}
    </div>
  );

  const tabsBody = renderTabKpis(activeTab.scope);

  const body = (
    <>
      {dashboardControls}
      {dashLayout === 'stack' ? stackBody : tabsBody}
    </>
  );

  if (embedded) {
    return (
      <div className="min-w-0 bg-white rounded-xl shadow-sm border border-gray-200 p-5">
        {body}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <SystemHeader breadcrumbs={[{ label: '業務報告' }]} />
      <div className="max-w-7xl mx-auto px-6 py-8">{body}</div>
    </div>
  );
};

export default BusinessReport;
